/**
 * CustomizationPanel Component
 *
 * Container component that composes all QR code customization controls.
 * Displays ColorPicker and CenterToggle always, and conditionally shows
 * BayLabelConfig and LogoUpload when center customization is enabled.
 *
 * Requirements:
 * - 4.5: When the toggle is enabled, reveal additional center customization options
 * - 5.1: When center customization is enabled, display bay label configuration options
 * - 6.1: When center customization is enabled, display a logo upload control
 */

import {useCallback, useEffect, useRef} from 'react';
import type {FontFamily, QrTemplate} from '../types';
import {ColorPicker} from './ColorPicker';
import {CenterToggle} from './CenterToggle';
import {CenterPaddingSlider} from './CenterPaddingSlider';
import {BayLabelConfig} from './BayLabelConfig';
import {LogoUpload} from './LogoUpload';
import './CustomizationPanel.css';

/** Path to the default logo image */
const DEFAULT_LOGO_PATH = '/ttr-logo.png';

/**
 * Props for the CustomizationPanel component
 */
export interface CustomizationPanelProps {
    /** Current QR template configuration */
    readonly template: QrTemplate;
    /** Whether the site has fixed bays (determines if bay label settings are available) */
    readonly hasFixedBays: boolean;
    /** Callback fired when template settings change */
    readonly onTemplateChange: (updates: Partial<QrTemplate>) => void;
    /** Callback fired when a logo file is uploaded */
    readonly onLogoUpload: (file: File) => void;
    /** Callback fired when the logo is removed */
    readonly onLogoRemove: () => void;
    /** Callback fired when reset to defaults is clicked */
    readonly onReset: () => void;
}

/**
 * CustomizationPanel - Container for all customization controls
 *
 * Composes ColorPicker, CenterToggle, BayLabelConfig, and LogoUpload components.
 * The center customization options (BayLabelConfig and LogoUpload) are only
 * visible when the center toggle is enabled.
 *
 * For fixed bays, users can choose between showing bay labels or logos.
 * For site/super bay QRs, only logo is available (no bay labels).
 *
 * @example
 * ```tsx
 * <CustomizationPanel
 *   template={template}
 *   hasFixedBays={true}
 *   onTemplateChange={(updates) => updateTemplate(updates)}
 *   onLogoUpload={(file) => handleLogoUpload(file)}
 *   onLogoRemove={() => handleLogoRemove()}
 * />
 * ```
 */
export function CustomizationPanel({
                                       template,
                                       hasFixedBays,
                                       onTemplateChange,
                                       onLogoUpload,
                                       onLogoRemove,
                                       onReset,
                                   }: CustomizationPanelProps): JSX.Element {
    // Track if we've loaded the default logo to avoid re-loading on every render
    const defaultLogoLoadedRef = useRef(false);

    // Reset the default-logo-loaded flag whenever the logo is cleared,
    // so the auto-load effect can fire again (e.g. on a second "reset to defaults").
    useEffect(() => {
        if (!template.logoDataUrl) {
            defaultLogoLoadedRef.current = false;
        }
    }, [template.logoDataUrl]);

    // Load default logo when center is enabled and no logo is set
    useEffect(() => {
        if (template.centerEnabled && !template.logoDataUrl && !defaultLogoLoadedRef.current) {
            defaultLogoLoadedRef.current = true;
            // Fetch the default logo and convert to data URL
            fetch(DEFAULT_LOGO_PATH)
                .then((response) => response.blob())
                .then((blob) => {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                        const dataUrl = event.target?.result as string;
                        onTemplateChange({ logoDataUrl: dataUrl });
                    };
                    reader.readAsDataURL(blob);
                })
                .catch((error) => {
                    console.error('Failed to load default logo:', error);
                });
        }
    }, [template.centerEnabled, template.logoDataUrl, onTemplateChange]);

    // Color change handler
    const handleColorChange = useCallback(
        (color: string) => {
            onTemplateChange({color});
        },
        [onTemplateChange]
    );

    // Center toggle change handler
    const handleCenterEnabledChange = useCallback(
        (centerEnabled: boolean) => {
            onTemplateChange({centerEnabled});
        },
        [onTemplateChange]
    );

    // Show bay label toggle handler
    const handleShowBayLabelChange = useCallback(
        (showBayLabel: boolean) => {
            onTemplateChange({showBayLabel});
        },
        [onTemplateChange]
    );

    // Font family change handler
    const handleFontFamilyChange = useCallback(
        (fontFamily: FontFamily) => {
            onTemplateChange({fontFamily});
        },
        [onTemplateChange]
    );

    // Font size change handler
    const handleFontSizeChange = useCallback(
        (fontSize: number) => {
            onTemplateChange({fontSize});
        },
        [onTemplateChange]
    );

    // Bay label color change handler
    const handleBayLabelColorChange = useCallback(
        (bayLabelColor: string) => {
            onTemplateChange({bayLabelColor});
        },
        [onTemplateChange]
    );

    // Center padding change handler
    const handleCenterPaddingChange = useCallback(
        (centerPadding: number) => {
            onTemplateChange({centerPadding});
        },
        [onTemplateChange]
    );

    // Corner label toggle handler
    const handleShowCornerLabelChange = useCallback(
        (showCornerLabel: boolean) => {
            onTemplateChange({showCornerLabel});
        },
        [onTemplateChange]
    );

    // Bottom logo toggle handler
    const handleBottomLogoChange = useCallback(
        (bottomLogo: boolean) => {
            onTemplateChange({bottomLogo});
        },
        [onTemplateChange]
    );

    // Bay label font settings are disabled when showBayLabel is off
    const bayLabelFontDisabled = !template.showBayLabel;
    const bayLabelFontDisabledReason = !template.showBayLabel
        ? 'Enable bay labels to configure font settings.'
        : undefined;

    return (
        <div className="customization-panel">
            <div className="customization-panel__header">
                <h3 className="customization-panel__title">Customize QR Code</h3>
                <button
                    type="button"
                    className="customization-panel__reset-button"
                    onClick={onReset}
                    aria-label="Reset to defaults"
                >
                    Reset to defaults
                </button>
            </div>

            <div className="customization-panel__section">
                <ColorPicker color={template.color} onChange={handleColorChange}/>
            </div>

            <div className="customization-panel__divider"/>

            <div className="customization-panel__section">
                <div className="customization-panel__corner-label-toggle">
                    <label className="customization-panel__toggle-label">
                        <input
                            type="checkbox"
                            className="customization-panel__toggle-input"
                            checked={template.showCornerLabel}
                            onChange={(e) => handleShowCornerLabelChange(e.target.checked)}
                        />
                        <span className="customization-panel__toggle-text">
              Show ID Label in Corner
            </span>
                    </label>
                    <p className="customization-panel__toggle-hint">
                        Displays bay ID or site name in the bottom-right corner of each QR code.
                    </p>
                </div>
            </div>

            <div className="customization-panel__divider"/>

            <div className="customization-panel__section">
                <div className="customization-panel__bottom-logo-toggle">
                    <label className="customization-panel__toggle-label">
                        <input
                            type="checkbox"
                            className="customization-panel__toggle-input"
                            checked={template.bottomLogo}
                            onChange={(e) => handleBottomLogoChange(e.target.checked)}
                        />
                        <span className="customization-panel__toggle-text">
              Show TGO Logo Below QR Code
            </span>
                    </label>
                    <p className="customization-panel__toggle-hint">
                        Displays the Toptracer Go logo beneath each QR code.
                    </p>
                </div>
            </div>

            <div className="customization-panel__divider"/>

            <div className="customization-panel__section">
                <CenterToggle
                    enabled={template.centerEnabled}
                    onChange={handleCenterEnabledChange}
                />
            </div>

            {template.centerEnabled && (
                <>
                    {/* Bay Label Toggle - only shown when site has fixed bays */}
                    {hasFixedBays && (
                        <>
                            <div className="customization-panel__divider"/>

                            <div className="customization-panel__section">
                                <div className="customization-panel__bay-label-toggle">
                                    <label className="customization-panel__toggle-label">
                                        <input
                                            type="checkbox"
                                            className="customization-panel__toggle-input"
                                            checked={template.showBayLabel}
                                            onChange={(e) => handleShowBayLabelChange(e.target.checked)}
                                        />
                                        <span className="customization-panel__toggle-text">
                      Show Bay Number on Fixed Bay QR Codes
                    </span>
                                    </label>
                                    <p className="customization-panel__toggle-hint">
                                        When enabled, fixed bay QR codes display the bay number instead of the logo.
                                    </p>
                                </div>
                            </div>

                            {/* Bay Label Font Settings - only shown when showBayLabel is enabled */}
                            {template.showBayLabel && (
                                <>
                                    <div className="customization-panel__divider"/>

                                    <div className="customization-panel__section customization-panel__center-options">
                                        <BayLabelConfig
                                            fontFamily={template.fontFamily}
                                            fontSize={template.fontSize}
                                            fontColor={template.bayLabelColor}
                                            disabled={bayLabelFontDisabled}
                                            disabledReason={bayLabelFontDisabledReason}
                                            onFontFamilyChange={handleFontFamilyChange}
                                            onFontSizeChange={handleFontSizeChange}
                                            onFontColorChange={handleBayLabelColorChange}
                                        />
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    <div className="customization-panel__divider"/>

                    <div className="customization-panel__section">
                        <LogoUpload
                            logoDataUrl={template.logoDataUrl}
                            onUpload={onLogoUpload}
                            onRemove={onLogoRemove}
                        />
                        {template.logoDataUrl && (
                            <div className="customization-panel__logo-padding">
                                <CenterPaddingSlider
                                    padding={template.centerPadding}
                                    onChange={handleCenterPaddingChange}
                                />
                            </div>
                        )}
                        {hasFixedBays && template.showBayLabel && (
                            <p className="customization-panel__logo-hint">
                                Logo will appear on Site and Super Bay QR codes. Fixed Bay QR codes will show the bay
                                number instead.
                            </p>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
